/**
 * sidebarForcedRail.ts — the auto-collapse (Sidebar v2 slice S3b, RULED
 * 2026-10-11 (1), (2)): below 1280 CSS px the rail is FORCED. "Below 1280"
 * means 1279.98px and under, so a viewport of exactly 1280 stays expanded.
 * Read SYNCHRONOUSLY at the first render (useSyncExternalStore's snapshot), so
 * a narrow window never paints the expanded sidebar first, and re-read on each
 * change of the query. Where matchMedia does not exist (jsdom, an old engine)
 * the rail is never forced. Below 768 the sidebar is the S3c drawer instead.
 */
import { useSyncExternalStore } from 'react'

export const SIDEBAR_FORCED_RAIL_QUERY = '(max-width: 1279.98px)'
/** Sidebar v2 S3c (RULED 2026-10-11): below 768 — 767.98px and under — the
 *  sidebar is a DRAWER (closed at the first render). Same synchronous read. */
export const SIDEBAR_DRAWER_QUERY = '(max-width: 767.98px)'

function mediaQuery(query: string): MediaQueryList | null {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query)
      : null
  } catch {
    return null
  }
}

/** One stable subscribe/snapshot pair per query (useSyncExternalStore
 *  resubscribes whenever `subscribe` changes identity). */
function mediaStore(query: string) {
  const subscribe = (onChange: () => void): (() => void) => {
    const mql = mediaQuery(query)
    if (!mql) return () => {}
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    mql.addListener(onChange)   // Safari < 14
    return () => mql.removeListener(onChange)
  }
  const snapshot = () => mediaQuery(query)?.matches ?? false
  return { subscribe, snapshot }
}

const forcedRail = mediaStore(SIDEBAR_FORCED_RAIL_QUERY)
const drawer = mediaStore(SIDEBAR_DRAWER_QUERY)

export function useForcedRail(): boolean {
  return useSyncExternalStore(forcedRail.subscribe, forcedRail.snapshot, () => false)
}

export function useSidebarDrawerMode(): boolean {
  return useSyncExternalStore(drawer.subscribe, drawer.snapshot, () => false)
}
